import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthUser, UserRole } from "@/types";

// =============================================================================
// Types
// =============================================================================

interface AuthState {
	user: AuthUser | null;
	token: string | null;
	isAuthenticated: boolean;
}

interface AuthActions {
	login: (user: AuthUser, token: string) => void;
	logout: () => void;
	updateUser: (user: Partial<AuthUser>) => void;
	hasRole: (role: UserRole) => boolean;
	hasAnyRole: (roles: UserRole[]) => boolean;
	canEdit: () => boolean;
	canDelete: () => boolean;
}

type AuthStore = AuthState & AuthActions;

// =============================================================================
// Initial State
// =============================================================================

const initialState: AuthState = {
	user: null,
	token: null,
	isAuthenticated: false,
};

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthStore>()(
	persist(
		(set, get) => ({
			...initialState,

			login: (user, token) => {
				set({
					user,
					token,
					isAuthenticated: true,
				});
			},

			logout: () => {
				// Clear refresh token from localStorage
				localStorage.removeItem("refreshToken");
				set(initialState);
			},

			updateUser: (updates) => {
				const { user } = get();
				if (user) {
					set({ user: { ...user, ...updates } });
				}
			},

			hasRole: (role) => {
				const { user } = get();
				return user?.role === role;
			},

			hasAnyRole: (roles) => {
				const { user } = get();
				return user ? roles.includes(user.role) : false;
			},

			canEdit: () => {
				const { user } = get();
				return user?.role === "admin" || user?.role === "operator";
			},

			canDelete: () => {
				const { user } = get();
				return user?.role === "admin";
			},
		}),
		{
			name: "auth-storage",
			partialize: (state) => ({
				user: state.user,
				token: state.token,
				isAuthenticated: state.isAuthenticated,
			}),
		},
	),
);
